import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Send,
  PenLine,
  Building,
  Calendar,
  User,
  Upload,
  FileCheck,
  MapPin,
  Edit,
  ArrowRight,
  Check,
} from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  draft: 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20',
  pending_signature: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  signed: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  approved: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  rejected: 'bg-rose-500/10 text-rose-400 border border-rose-500/20',
};

// Onboarding Steps
const STEPS = {
  W9_UPLOAD: 'w9_upload',
  REVIEW_INFO: 'review_info',
  SIGNING_AUTHORITY: 'signing_authority',
  DOCUMENT_REVIEW: 'document_review',
  SIGNATURE: 'signature',
  COMPLETE: 'complete'
};

export default function VendorPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [currentStep, setCurrentStep] = useState(STEPS.W9_UPLOAD);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [changeRequest, setChangeRequest] = useState({ requested_change: '', section: '' });
  const [submitting, setSubmitting] = useState(false);
  const w9FileRef = useRef(null);
  
  // Vendor profile data (from W-9 extraction)
  const [vendorProfile, setVendorProfile] = useState({
    legal_name: '',
    business_name: '',
    tax_classification: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    tin_type: '',
    tin_number: '',
    signing_authority_name: '',
    signing_authority_position: ''
  });
  
  const [w9Extracted, setW9Extracted] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  useEffect(() => {
    fetchDocument();
  }, [token]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API_URL}/vendor-portal/document/${token}`);
      setDocumentData(response.data);
      
      // Check if vendor already completed onboarding
      if (response.data.vendor_profile?.w9_uploaded) {
        setW9Extracted(true);
        setVendorProfile(prev => ({
          ...prev,
          ...response.data.vendor_profile
        }));
        
        // Skip to appropriate step
        if (response.data.vendor_profile.signing_authority_name) {
          setCurrentStep(STEPS.DOCUMENT_REVIEW);
        } else {
          setCurrentStep(STEPS.SIGNING_AUTHORITY);
        }
      }
      
      // If already signed, show complete
      if (response.data.status === 'signed') {
        setCurrentStep(STEPS.COMPLETE);
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid or expired access link';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleW9Upload = async (e) => {
    e.preventDefault();
    const file = w9FileRef.current?.files[0];

    if (!file) {
      toast.error('Please select a file');
      return;
    }

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, PNG, or JPG');
      return;
    }

    setSubmitting(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const payload = {
          file_data: reader.result,
          file_name: file.name,
          file_type: file.type
        };
        
        const response = await axios.post(`${API_URL}/vendor-portal/document/${token}/w9`, payload);
        toast.success('W-9 uploaded successfully!');
        
        // If data was extracted, populate the form
        if (response.data.extracted_data) {
          const extracted = response.data.extracted_data;
          setVendorProfile(prev => ({
            ...prev,
            legal_name: extracted.legal_name || '',
            business_name: extracted.business_name || '',
            tax_classification: extracted.tax_classification || '',
            address: extracted.address || '',
            city: extracted.city || '',
            state: extracted.state || '',
            zip_code: extracted.zip_code || '',
            tin_type: extracted.tin_type || '',
            tin_number: extracted.tin_number || '' // Already masked from backend
          }));
          setW9Extracted(true);
          toast.success('Tax information extracted from W-9');
        }
        
        setCurrentStep(STEPS.REVIEW_INFO);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to upload W-9');
      } finally {
        setSubmitting(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleProfileUpdate = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/vendor-portal/document/${token}/update-profile`, vendorProfile);
      toast.success('Profile updated successfully');
      setEditingProfile(false);
      setCurrentStep(STEPS.SIGNING_AUTHORITY);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSigningAuthoritySubmit = async () => {
    if (!vendorProfile.signing_authority_name || !vendorProfile.signing_authority_position) {
      toast.error('Please enter signing authority name and position');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/vendor-portal/document/${token}/update-profile`, {
        signing_authority_name: vendorProfile.signing_authority_name,
        signing_authority_position: vendorProfile.signing_authority_position
      });
      toast.success('Signing authority saved');
      setCurrentStep(STEPS.DOCUMENT_REVIEW);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save signing authority');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSign = async (signatureData) => {
    setSubmitting(true);
    try {
      // Include signing authority info with signature
      const payload = {
        ...signatureData,
        signing_authority_name: vendorProfile.signing_authority_name,
        signing_authority_position: vendorProfile.signing_authority_position
      };
      
      await axios.post(`${API_URL}/vendor-portal/document/${token}/sign`, payload);
      toast.success('Document signed successfully!');
      setSignModalOpen(false);
      setCurrentStep(STEPS.COMPLETE);
      fetchDocument();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sign document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitChangeRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/vendor-portal/document/${token}/change-request`, changeRequest);
      toast.success('Change request submitted successfully');
      setChangeRequestOpen(false);
      setChangeRequest({ requested_change: '', section: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit change request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Access Error</h2>
            <p className="text-zinc-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { document, template, company } = documentData || {};
  const isSigned = document?.status === 'signed';
  const isPending = document?.status === 'pending_signature';

  // Progress indicator
  const steps = [
    { key: STEPS.W9_UPLOAD, label: 'Upload W-9', icon: Upload },
    { key: STEPS.REVIEW_INFO, label: 'Review Info', icon: FileCheck },
    { key: STEPS.SIGNING_AUTHORITY, label: 'Authority', icon: User },
    { key: STEPS.DOCUMENT_REVIEW, label: 'Review Doc', icon: FileText },
    { key: STEPS.SIGNATURE, label: 'Sign', icon: PenLine },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="vendor-portal">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-zinc-900/50 border-b border-zinc-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">Vault</h1>
              <p className="text-xs text-zinc-500">Secure Document Signing</p>
            </div>
          </div>
          <Badge className={statusColors[document?.status] || statusColors.draft}>
            {document?.status?.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
      </header>

      {/* Progress Steps */}
      {!isSigned && currentStep !== STEPS.COMPLETE && (
        <div className="bg-zinc-900/30 border-b border-zinc-800 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = step.key === currentStep;
                const isComplete = index < currentStepIndex;
                
                return (
                  <div key={step.key} className="flex items-center">
                    <div className={`flex items-center gap-2 ${
                      isActive ? 'text-blue-400' : isComplete ? 'text-emerald-400' : 'text-zinc-600'
                    }`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isActive ? 'bg-blue-500/20 border border-blue-500' : 
                        isComplete ? 'bg-emerald-500/20 border border-emerald-500' : 
                        'bg-zinc-800 border border-zinc-700'
                      }`}>
                        {isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <span className="text-xs hidden sm:inline">{step.label}</span>
                    </div>
                    {index < steps.length - 1 && (
                      <ArrowRight className={`w-4 h-4 mx-2 ${
                        index < currentStepIndex ? 'text-emerald-500' : 'text-zinc-700'
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Document Info Card */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  {documentData?.document_type === 'msa' ? 'Master Service Agreement' : 'Purchase Order'}
                </CardTitle>
                <CardDescription className="text-zinc-400 mt-1">
                  From {company?.name || 'Company'}
                </CardDescription>
              </div>
              <div className="text-right text-sm text-zinc-500">
                <p>Sent to: {documentData?.vendor_email}</p>
                <p className="text-xs mt-1">
                  {new Date(document?.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Step Content */}
        {currentStep === STEPS.W9_UPLOAD && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-amber-400" />
                Step 1: Upload Your W-9
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Please upload your completed W-9 tax form. We'll extract your information automatically.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleW9Upload} className="space-y-4">
                <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center hover:border-amber-500/50 transition-colors">
                  <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
                  <Input
                    ref={w9FileRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    className="bg-zinc-950 border-zinc-800 max-w-xs mx-auto"
                    data-testid="w9-file-input"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    Accepted formats: PDF, PNG, JPG (max 10MB)
                  </p>
                </div>
                
                <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <p className="text-xs text-amber-400">
                    Your W-9 information is securely stored and encrypted. Only the requesting company 
                    can access it for tax compliance purposes.
                  </p>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-amber-600 hover:bg-amber-500"
                  disabled={submitting}
                  data-testid="upload-w9-btn"
                >
                  {submitting ? 'Uploading...' : 'Upload W-9 & Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {currentStep === STEPS.REVIEW_INFO && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-blue-400" />
                Step 2: Review Extracted Information
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Please review the information extracted from your W-9. Click edit if corrections are needed.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {w9Extracted && (
                <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg mb-4">
                  <p className="text-xs text-emerald-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Information successfully extracted from your W-9
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Legal Name</Label>
                  {editingProfile ? (
                    <Input
                      value={vendorProfile.legal_name}
                      onChange={(e) => setVendorProfile({...vendorProfile, legal_name: e.target.value})}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  ) : (
                    <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                      {vendorProfile.legal_name || '-'}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400">Business Name</Label>
                  {editingProfile ? (
                    <Input
                      value={vendorProfile.business_name}
                      onChange={(e) => setVendorProfile({...vendorProfile, business_name: e.target.value})}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  ) : (
                    <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                      {vendorProfile.business_name || '-'}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400">Tax Classification</Label>
                  {editingProfile ? (
                    <Input
                      value={vendorProfile.tax_classification}
                      onChange={(e) => setVendorProfile({...vendorProfile, tax_classification: e.target.value})}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  ) : (
                    <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                      {vendorProfile.tax_classification || '-'}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400">TIN ({vendorProfile.tin_type || 'SSN/EIN'})</Label>
                  <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                    {vendorProfile.tin_number || '***-**-****'}
                  </p>
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-zinc-400">Address</Label>
                  {editingProfile ? (
                    <Input
                      value={vendorProfile.address}
                      onChange={(e) => setVendorProfile({...vendorProfile, address: e.target.value})}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  ) : (
                    <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                      {vendorProfile.address || '-'}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400">City</Label>
                  {editingProfile ? (
                    <Input
                      value={vendorProfile.city}
                      onChange={(e) => setVendorProfile({...vendorProfile, city: e.target.value})}
                      className="bg-zinc-950 border-zinc-800"
                    />
                  ) : (
                    <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                      {vendorProfile.city || '-'}
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">State</Label>
                    {editingProfile ? (
                      <Input
                        value={vendorProfile.state}
                        onChange={(e) => setVendorProfile({...vendorProfile, state: e.target.value})}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    ) : (
                      <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                        {vendorProfile.state || '-'}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-400">ZIP Code</Label>
                    {editingProfile ? (
                      <Input
                        value={vendorProfile.zip_code}
                        onChange={(e) => setVendorProfile({...vendorProfile, zip_code: e.target.value})}
                        className="bg-zinc-950 border-zinc-800"
                      />
                    ) : (
                      <p className="text-white bg-zinc-800/50 rounded px-3 py-2">
                        {vendorProfile.zip_code || '-'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                {editingProfile ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setEditingProfile(false)}
                      className="border-zinc-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleProfileUpdate}
                      className="bg-blue-600 hover:bg-blue-500"
                      disabled={submitting}
                    >
                      {submitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setEditingProfile(true)}
                      className="border-zinc-700"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Information
                    </Button>
                    <Button
                      onClick={() => setCurrentStep(STEPS.SIGNING_AUTHORITY)}
                      className="bg-blue-600 hover:bg-blue-500"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === STEPS.SIGNING_AUTHORITY && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-purple-400" />
                Step 3: Signing Authority
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Enter the name and position of the person authorized to sign this agreement.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Signing Authority Name *</Label>
                <Input
                  value={vendorProfile.signing_authority_name}
                  onChange={(e) => setVendorProfile({...vendorProfile, signing_authority_name: e.target.value})}
                  placeholder="John Smith"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="signing-authority-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Position / Title *</Label>
                <Input
                  value={vendorProfile.signing_authority_position}
                  onChange={(e) => setVendorProfile({...vendorProfile, signing_authority_position: e.target.value})}
                  placeholder="CEO, Director, Manager, etc."
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="signing-authority-position"
                />
              </div>
              
              <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                <p className="text-xs text-purple-400">
                  This information will be used to identify the signatory on the agreement and 
                  will appear on the final signed document.
                </p>
              </div>
              
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(STEPS.REVIEW_INFO)}
                  className="border-zinc-700"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSigningAuthoritySubmit}
                  className="bg-purple-600 hover:bg-purple-500"
                  disabled={submitting || !vendorProfile.signing_authority_name || !vendorProfile.signing_authority_position}
                >
                  {submitting ? 'Saving...' : 'Continue'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === STEPS.DOCUMENT_REVIEW && (
          <>
            <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Step 4: Review Document
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Please review the agreement carefully before signing.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Vendor Info Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-zinc-800/30 rounded-lg">
                  <div>
                    <p className="text-xs text-zinc-500">Vendor</p>
                    <p className="text-sm text-white">{vendorProfile.legal_name || vendorProfile.business_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Signing As</p>
                    <p className="text-sm text-white">{vendorProfile.signing_authority_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Position</p>
                    <p className="text-sm text-white">{vendorProfile.signing_authority_position || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">TIN</p>
                    <p className="text-sm text-white">{vendorProfile.tin_number || '***-**-****'}</p>
                  </div>
                </div>
                
                {/* Document Content */}
                <div className="bg-white rounded-lg p-6 max-h-96 overflow-y-auto">
                  <div 
                    className="prose prose-sm max-w-none"
                    style={{ color: '#000' }}
                    dangerouslySetInnerHTML={{ 
                      __html: template?.content?.replace(/\n/g, '<br/>') || document?.content?.replace(/\n/g, '<br/>') || 'Document content not available' 
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isPending && (
                <>
                  <Button
                    onClick={() => {
                      setCurrentStep(STEPS.SIGNATURE);
                      setSignModalOpen(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)] px-8"
                    data-testid="sign-document-btn"
                  >
                    <PenLine className="w-4 h-4 mr-2" />
                    Sign Document
                  </Button>
                  <Button
                    onClick={() => setChangeRequestOpen(true)}
                    variant="outline"
                    className="border-zinc-700 hover:bg-zinc-800"
                    data-testid="request-changes-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Request Changes
                  </Button>
                </>
              )}
            </div>

            {/* Legal Notice */}
            <div className="mt-8 p-4 bg-zinc-900/30 border border-zinc-800 rounded-lg">
              <p className="text-xs text-zinc-500 text-center">
                By signing this document electronically, you acknowledge that your electronic signature
                is legally binding under the U.S. Electronic Signatures in Global and National Commerce
                Act (ESIGN) and the Uniform Electronic Transactions Act (UETA).
              </p>
            </div>
          </>
        )}

        {currentStep === STEPS.COMPLETE && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Document Signed Successfully!</h2>
              <p className="text-zinc-400 mb-6">
                Thank you for signing. A copy of the signed document will be sent to your email.
              </p>
              
              <div className="bg-zinc-800/30 rounded-lg p-4 max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-xs text-zinc-500">Signed By</p>
                    <p className="text-sm text-white">{vendorProfile.signing_authority_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Position</p>
                    <p className="text-sm text-white">{vendorProfile.signing_authority_position}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Company</p>
                    <p className="text-sm text-white">{vendorProfile.legal_name || vendorProfile.business_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Date</p>
                    <p className="text-sm text-white">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Signature Modal */}
      <Dialog open={signModalOpen} onOpenChange={setSignModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Sign Document</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Signing as <span className="text-white font-medium">{vendorProfile.signing_authority_name}</span>, 
              {' '}<span className="text-zinc-500">{vendorProfile.signing_authority_position}</span>
            </DialogDescription>
          </DialogHeader>
          <SignaturePad
            onSave={handleSign}
            onCancel={() => setSignModalOpen(false)}
            signerName={vendorProfile.signing_authority_name}
          />
        </DialogContent>
      </Dialog>

      {/* Change Request Modal */}
      <Dialog open={changeRequestOpen} onOpenChange={setChangeRequestOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Request Changes</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Describe the changes you'd like to request for this document
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitChangeRequest} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Section (Optional)</Label>
              <Input
                value={changeRequest.section}
                onChange={(e) => setChangeRequest({ ...changeRequest, section: e.target.value })}
                placeholder="e.g., Payment Terms, Section 4.2"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Requested Changes *</Label>
              <Textarea
                value={changeRequest.requested_change}
                onChange={(e) => setChangeRequest({ ...changeRequest, requested_change: e.target.value })}
                placeholder="Describe the changes you're requesting..."
                className="bg-zinc-950 border-zinc-800 min-h-[120px]"
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setChangeRequestOpen(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-500"
                disabled={submitting || !changeRequest.requested_change}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
